/**
 * NAS Advisories card — polls the backend for FAA SWIM/TFMS advisories
 * (GDPs, Ground Stops, AFPs, EDCTs). Two ways to use it:
 *   <NasAdvisoriesCard hasActive />          the active flight's airports (Live Tracking)
 *   <NasAdvisoriesCard orig="CYYZ" dest="KEWR" />  any two airports (Flight Plan,
 *                                            before or after INIT)
 * Renders nothing when there is nothing to show. TFMS covers the US system,
 * so only flights touching a US airport (or a US program) can show anything.
 */
import { useQuery } from "@tanstack/react-query";
import { faa } from "../lib/api";
import { AlertTriangle, Clock, MapPin, Radio } from "lucide-react";

const KIND_META = {
  GDP:        { label: "Ground Delay Program",  tone: "amber" },
  GS:         { label: "Ground Stop",           tone: "red"   },
  AFP:        { label: "Airspace Flow Program", tone: "amber" },
  EDCT:       { label: "EDCT Assigned",         tone: "blue"  },
  TMI:        { label: "Traffic Mgmt Initiative", tone: "amber" },
  TMI_FLIGHT: { label: "TMI · Flight in restriction", tone: "amber" },
  OTHER:      { label: "Advisory",              tone: "muted" },
};

// Dark text on the light theme, the pale text on the dark one (it was the
// pale text on both, unreadable on white, 2026-10-18). Background /10: /8 is
// not an opacity step Tailwind generates, so there was no tint at all.
const TONE_STYLES = {
  red:   "border-red-500/40   bg-red-500/10   text-red-800   dark:text-red-200",
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200",
  blue:  "border-sky-500/40   bg-sky-500/10   text-sky-900   dark:text-sky-200",
  muted: "border-border       bg-secondary    text-muted-foreground",
};

export function NasAdvisoriesCard({ hasActive, orig, dest, flt, opr }) {
  const byAirports = !!(orig || dest);
  const { data } = useQuery({
    queryKey: byAirports ? ["faa", "airports", orig || "", dest || "", flt || "", opr || ""] : ["faa", "for-active"],
    queryFn: byAirports
      ? () => faa.advisories(orig || undefined, dest || undefined, flt || undefined, opr || undefined)
      : faa.forActive,
    enabled: byAirports || !!hasActive,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });

  if (!byAirports && !hasActive) return null;
  const items = data?.items || [];
  if (items.length === 0) return null;
  const shownOrig = byAirports ? orig : data?.orig;
  const shownDest = byAirports ? dest : data?.dest;

  return (
    <section className="card-flat p-5 mb-6" data-testid="nas-advisories">
      <div className="flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold tracking-wider uppercase">
          FAA NAS Advisories
        </h3>
        <span className="text-xs text-muted-foreground">
          {items.length} affecting {shownOrig}
          {shownOrig && shownDest ? " / " : ""}
          {shownDest}
        </span>
      </div>

      <ul className="space-y-2">
        {items.slice(0, 6).map((a, i) => {
          const meta = KIND_META[a.kind] || KIND_META.OTHER;
          return (
            <li
              key={`${a.received_at}-${i}`}
              className={`flex items-start gap-3 p-3 rounded-md border ${TONE_STYLES[meta.tone]}`}
              data-testid={`nas-advisory-${a.kind.toLowerCase()}`}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                  <span>{meta.label}</span>
                  {a.airports?.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-medium text-foreground/70">
                      <MapPin className="w-3 h-3" />
                      {a.airports.slice(0, 4).join(" · ")}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-foreground/80 truncate">
                  {a.summary}
                </div>
                {(a.start || a.end) && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-foreground/60">
                    <Clock className="w-3 h-3" />
                    {a.start || "—"}
                    {a.end ? ` → ${a.end}` : ""}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
